import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Button,
  Divider,
} from "@mui/material";
import { toast } from "react-toastify";
import { useUserData } from "features/User/hooks/user-data.hook";
import { fetchCitationStyles } from "features/Bookmarks/services/citation.service";

const CitationStylePreference = () => {
  const { userPreferences, upsertUserPreference } = useUserData();
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState("");

  useEffect(() => {
    const getStyles = async () => {
      try {
        const data = await fetchCitationStyles();
        const options = data.map((style) => ({
          name: style.title,
          key: style.name,
        }));
        setStyles(options);
      } catch (error) {
        console.error("Failed to fetch citation styles", error);
      } finally {
        setLoading(false);
      }
    };
    getStyles();
  }, []);

  useEffect(() => {
    if (userPreferences?.defaultCitationStyle) {
      setSelectedStyle(userPreferences.defaultCitationStyle);
    } else {
      setSelectedStyle("apa");
    }
  }, [userPreferences]);

  const handleChange = (event) => {
    setSelectedStyle(event.target.value);
  };

  const handleSave = async () => {
    setSaveLoading(true);
    const { userAvatar, ...restPreferences } = userPreferences || {};
    try {
      await upsertUserPreference({
        ...restPreferences,
        defaultCitationStyle: selectedStyle,
      });
      const selectedStyleName = styles.find((s) => s.key === selectedStyle)?.name || selectedStyle;
      toast.success(`Default Citation updated to ${selectedStyleName} successfully!`);
    } catch (error) {
      toast.error("Failed to update default citation");
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: { sm: "100%", md: "60%" }, mb: 2 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Default Citation Style
      </Typography>
      <Typography sx={{ mb: 2 }}>
        By default, the citation style generated for our bookmarks is APA. If
        you would like the default to be different, you can select from any of
        our available citation styles below. You will always be able to change
        this on individual citations when generating a citation in a bookmark.
      </Typography>
      <FormControl fullWidth sx={{ maxWidth: 400 }}>
        <InputLabel id="citation-style-label">Select Citation Style</InputLabel>
        <Select
          labelId="citation-style-label"
          id="citation-style-select"
          value={selectedStyle || "apa"}
          label="Select Citation Style"
          onChange={handleChange}
        >
          {styles.map((style) => (
            <MenuItem key={style.key} value={style.key}>
              {style.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          onClick={handleSave}
          disabled={saveLoading}
          sx={{ display: "flex", gap: 1 }}
        >
          {saveLoading && <CircularProgress size={20} />}
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default CitationStylePreference;
